import { useCallback, useEffect, useRef } from "react";
import { document } from "@packages/global";

export function usePortal({ id }: { id: string }) {
  const rootElemRef = useRef<HTMLElement | null>(null);

  const createRootElement = useCallback((id: string) => {
    const rootElement = document.createElement("div");
    rootElement.setAttribute("id", id);
    return rootElement;
  }, []);

  const addRootElement = useCallback((rootElem: HTMLElement) => {
    document.body.insertBefore(
      rootElem,
      document.body.lastElementChild?.nextElementSibling ?? null
    );
  }, []);

  useEffect(() => {
    const existingParent = document.getElementById(id);
    const parentElem = existingParent || createRootElement(id);

    if (!existingParent) addRootElement(parentElem);

    if (rootElemRef.current) {
      parentElem.appendChild(rootElemRef.current);
    }

    return () => {
      if (rootElemRef.current) {
        rootElemRef.current.remove();
      }
      if (!existingParent) parentElem.remove();
    };
  }, [id, createRootElement, addRootElement]);

  function getRootElem() {
    if (!rootElemRef?.current) {
      rootElemRef.current = document.createElement("div");
    }
    return rootElemRef.current;
  }

  return getRootElem();
}
